// State + transitions for MemberHandicapHistory. Consolidates the async load,
// score mutations, and UI toggles into a single reducer (REACT-093) and keeps
// all derivation/filtering in selectors rather than the component (REACT-092).

export const INITIAL_STATE = {
  loading: true,
  error: null,
  summary: null,
  saving: false,
  mutationError: null,
  editedHi: null,
  selectedHoles: '18',
  activeTab: 'active',
  showExplain: false,
  showAddForm: false,
}

export const buildInitialState = (initialHi) => ({ ...INITIAL_STATE, editedHi: initialHi || null })

// Private: every transition is reached through an action creator below, so the
// types stay an implementation detail of this module.
const ACTION_TYPES = {
  LOAD_START: 'LOAD_START',
  LOAD_SUCCESS: 'LOAD_SUCCESS',
  LOAD_ERROR: 'LOAD_ERROR',
  MUTATION_START: 'MUTATION_START',
  MUTATION_SUCCESS: 'MUTATION_SUCCESS',
  MUTATION_ERROR: 'MUTATION_ERROR',
  SET_EDITED_HI: 'SET_EDITED_HI',
  SET_SELECTED_HOLES: 'SET_SELECTED_HOLES',
  SET_ACTIVE_TAB: 'SET_ACTIVE_TAB',
  TOGGLE_EXPLAIN: 'TOGGLE_EXPLAIN',
  TOGGLE_ADD_FORM: 'TOGGLE_ADD_FORM',
  SET_ADD_FORM: 'SET_ADD_FORM',
}

export const handicapHistoryReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.LOAD_START:
      return { ...state, loading: true, error: null }
    case ACTION_TYPES.LOAD_SUCCESS:
      return { ...state, loading: false, summary: action.summary }
    case ACTION_TYPES.LOAD_ERROR:
      return { ...state, loading: false, error: action.error }
    case ACTION_TYPES.MUTATION_START:
      return { ...state, saving: true, mutationError: null }
    case ACTION_TYPES.MUTATION_SUCCESS:
      return {
        ...state,
        saving: false,
        summary: action.summary,
        showAddForm: action.closeAddForm ? false : state.showAddForm,
      }
    case ACTION_TYPES.MUTATION_ERROR:
      return { ...state, saving: false, mutationError: action.error }
    case ACTION_TYPES.SET_EDITED_HI:
      return { ...state, editedHi: action.value }
    case ACTION_TYPES.SET_SELECTED_HOLES:
      return { ...state, selectedHoles: action.value }
    case ACTION_TYPES.SET_ACTIVE_TAB:
      return { ...state, activeTab: action.value }
    case ACTION_TYPES.TOGGLE_EXPLAIN:
      return { ...state, showExplain: !state.showExplain }
    case ACTION_TYPES.TOGGLE_ADD_FORM:
      return { ...state, showAddForm: !state.showAddForm }
    case ACTION_TYPES.SET_ADD_FORM:
      return { ...state, showAddForm: action.value }
    default:
      return state
  }
}

// Action creators — callers dispatch through these rather than building action
// objects, so ACTION_TYPES never leaves this file. dispatch comes first, matching
// actions.js.

export const loadStart = (dispatch) => dispatch({ type: ACTION_TYPES.LOAD_START })

export const loadSuccess = (dispatch, summary) => dispatch({ type: ACTION_TYPES.LOAD_SUCCESS, summary })

export const loadError = (dispatch, error) => dispatch({ type: ACTION_TYPES.LOAD_ERROR, error })

export const mutationStart = (dispatch) => dispatch({ type: ACTION_TYPES.MUTATION_START })

// closeAddForm collapses the Add Score form on success; a delete leaves it as is.
export const mutationSuccess = (dispatch, summary, closeAddForm = false) =>
  dispatch({ type: ACTION_TYPES.MUTATION_SUCCESS, summary, closeAddForm })

export const mutationError = (dispatch, error) => dispatch({ type: ACTION_TYPES.MUTATION_ERROR, error })

export const setEditedHi = (dispatch, value) => dispatch({ type: ACTION_TYPES.SET_EDITED_HI, value })

export const setSelectedHoles = (dispatch, value) => dispatch({ type: ACTION_TYPES.SET_SELECTED_HOLES, value })

export const setActiveTab = (dispatch, value) => dispatch({ type: ACTION_TYPES.SET_ACTIVE_TAB, value })

export const toggleExplain = (dispatch) => dispatch({ type: ACTION_TYPES.TOGGLE_EXPLAIN })

export const toggleAddForm = (dispatch) => dispatch({ type: ACTION_TYPES.TOGGLE_ADD_FORM })

export const setAddForm = (dispatch, value) => dispatch({ type: ACTION_TYPES.SET_ADD_FORM, value })

// Selectors — pure derivation from summary/state (REACT-092)

const round1 = (n) => Math.round(n * 10) / 10

const sectionHasScores = (section) => {
  const scores = (section || {}).scores || {}
  return (scores.active || []).length > 0 || (scores.inactive || []).length > 0
}

export const getAvailableHoles = (summary) =>
  ['18', '9'].filter((h) => summary[`show_${h}`] && sectionHasScores(summary[h]))

export const getActiveHoles = (summary, selectedHoles) => {
  const available = getAvailableHoles(summary)
  return available.includes(selectedHoles) ? selectedHoles : (available[0] || '18')
}

export const getChartYears = (section) =>
  [...new Set((section.handicap_history || []).map((p) => p.year))].sort()

// Compares the TMS index against the HS handicap: the 18-hole value when
// available, otherwise (only 9-hole available) the 9-hole value doubled.
export const getHiMismatch = (summary, editedHi) => {
  const hi18 = (summary['18'] || {}).current_hi
  const hi9 = (summary['9'] || {}).current_hi
  const referenceHi = hi18 != null
    ? round1(parseFloat(hi18))
    : (hi9 != null ? round1(parseFloat(hi9) * 2) : null)
  return referenceHi != null && editedHi != null && round1(parseFloat(editedHi)) !== referenceHi
}
